/**
 * UserCancellationPageV2 — redesigned Cancelations Report.
 *
 * Layout: PageHeader + KPI cards + distribution chart + over-time
 * chart + collapsible raw events table.
 *
 * The KPI cards and the two Recharts-driven charts stay as the v1
 * sub-components for now (CancellationKpiCards / Distribution /
 * OverTime) — they're functional and a deep restyle of every
 * chart legend / bar tint is a follow-up. Each is wrapped in a v2
 * surface card so they don't clash with bg-bg.
 *
 * The raw events table is reimplemented inline as a LibTable so
 * the primary data view IS v2.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserCancellationReports } from '../../../../actions/userCancellationActions';
import { exportCancellationDataCSV } from '../../../../actions/exportCSVActions';
import CancellationDistributionChart from '../CancellationDistributionChart';
import CancellationKpiCards from '../CancellationKpiCards';
import CancellationOverTimeChart from '../CancellationOverTimeChart';
import {
  DEFAULT_PER_PAGE,
  PARSE_INT_RADIX,
} from '../../../../constants/pagination';
import {
  PageHeader,
  Button,
  LibTable,
  cn,
} from '../../../redesign';
import { Pager } from '../../RequestsPage/v2/RequestsPageV2';

const SearchIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const DownloadIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

function formatDateTime(epochSeconds) {
  if (!epochSeconds) return { date: '—', time: '' };
  const dt = new Date(epochSeconds * 1000);
  return {
    date: dt.toLocaleDateString(),
    time: dt.toLocaleTimeString(),
  };
}

/* Small wrapper that gives any v1 sub-component a v2 brand
 * surface so it doesn't sit on bare bg-bg. */
function ChartCard({ title, children }) {
  return (
    <div
      className="bg-surface border border-sand"
      style={{ borderRadius: 12, padding: 16, marginBottom: 16, overflow: 'hidden' }}
    >
      {title && (
        <div
          className="font-condensed font-bold uppercase text-text-muted"
          style={{ fontSize: 10.5, letterSpacing: '0.08em', marginBottom: 10 }}
        >
          {title}
        </div>
      )}
      {children}
    </div>
  );
}

export default function UserCancellationPageV2() {
  const dispatch = useDispatch();
  const { user_cancellation_reports, pagination } = useSelector(
    (s) => s.user_cancellations,
  );
  const distributionData = useSelector(
    (s) => s.user_cancellations.distribution.data,
  );
  const distributionLoading = useSelector(
    (s) => s.user_cancellations.distribution.loading,
  );

  const [searchText, setSearchText] = useState('');
  const [rawEventsOpen, setRawEventsOpen] = useState(false);
  const [period, setPeriod] = useState('last_30_days');
  const [year, setYear] = useState(new Date().getFullYear() - 1);

  const indexFilters = useMemo(
    () => ({ period, year: period === 'year' ? year : undefined }),
    [period, year],
  );

  const lastFiltersFetched = useRef(null);
  useEffect(() => {
    if (!rawEventsOpen) return;
    const key = JSON.stringify(indexFilters);
    if (lastFiltersFetched.current === key) return;
    lastFiltersFetched.current = key;
    dispatch(
      fetchUserCancellationReports(1, DEFAULT_PER_PAGE, indexFilters),
    );
  }, [rawEventsOpen, dispatch, indexFilters]);

  const filtered = useMemo(() => {
    if (!Array.isArray(user_cancellation_reports)) return [];
    if (!searchText) return user_cancellation_reports;
    const lc = searchText.toLowerCase();
    return user_cancellation_reports.filter(
      (r) =>
        (r.reason || '').toLowerCase().includes(lc) ||
        (r.user_email || '').toLowerCase().includes(lc) ||
        (r.user_full_name || '').toLowerCase().includes(lc),
    );
  }, [user_cancellation_reports, searchText]);

  const handlePrev = () => {
    if (!pagination || pagination.page <= 1) return;
    dispatch(
      fetchUserCancellationReports(
        pagination.page - 1,
        pagination.page_size,
        indexFilters,
      ),
    );
  };
  const handleNext = () => {
    if (!pagination || pagination.page >= pagination.total_pages) return;
    dispatch(
      fetchUserCancellationReports(
        pagination.page + 1,
        pagination.page_size,
        indexFilters,
      ),
    );
  };
  const handlePerPage = (e) => {
    dispatch(
      fetchUserCancellationReports(
        1,
        parseInt(e.target.value, PARSE_INT_RADIX),
        indexFilters,
      ),
    );
  };

  // Pager expects `per_page`, but this slice's reducer stores `page_size`.
  // Map for display.
  const pagerPagination = pagination
    ? {
        ...pagination,
        per_page: pagination.page_size,
      }
    : null;

  return (
    <div className="bg-bg min-h-full">
      <PageHeader
        eyebrow="Reports"
        title="Cancelations Report"
        actions={
          <Button
            variant="ghost"
            leadingIcon={DownloadIcon}
            onClick={() => dispatch(exportCancellationDataCSV())}
          >
            Export CSV
          </Button>
        }
      />

      <div style={{ padding: '0 28px 60px' }}>
        <ChartCard>
          <CancellationKpiCards
            data={distributionData}
            loading={distributionLoading}
          />
        </ChartCard>

        <ChartCard title="Cancellation reasons · distribution">
          <CancellationDistributionChart
            period={period}
            year={year}
            onPeriodChange={setPeriod}
            onYearChange={setYear}
          />
        </ChartCard>

        <ChartCard title="Cancellations over time">
          <CancellationOverTimeChart />
        </ChartCard>

        {/* Raw events drawer — primary view is the charts; this
         *  lets operators drill into a specific row when needed. */}
        <div style={{ marginTop: 8 }}>
          <button
            type="button"
            onClick={() => setRawEventsOpen((o) => !o)}
            className={cn('flex items-center font-condensed font-bold uppercase text-navy')}
            style={{
              gap: 6,
              fontSize: 11,
              letterSpacing: '0.08em',
              padding: '8px 14px',
              borderRadius: 999,
              border: '1px solid #E8E0CF',
              background: '#FAF4EA',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <span style={{ fontSize: 10 }}>{rawEventsOpen ? '▾' : '▸'}</span>
            <span>{rawEventsOpen ? 'Hide raw events' : 'Show raw events'}</span>
          </button>
        </div>

        {rawEventsOpen && (
          <div style={{ marginTop: 16 }}>
            <div
              className="flex items-center"
              style={{ gap: 12, padding: '0 4px 12px' }}
            >
              <div
                className="flex items-center bg-cream border border-sand"
                style={{ borderRadius: 8, padding: '0 12px', gap: 8, flex: 1, maxWidth: 480 }}
              >
                <span className="text-text-muted shrink-0">{SearchIcon}</span>
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search raw events…"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: 0,
                    background: 'transparent',
                    outline: 'none',
                    fontFamily: 'inherit',
                    fontSize: 13,
                    color: '#2C3E50',
                    padding: '8px 0',
                  }}
                />
                {searchText && (
                  <button
                    type="button"
                    onClick={() => setSearchText('')}
                    style={{
                      background: 'transparent',
                      border: 0,
                      cursor: 'pointer',
                      fontSize: 13,
                      padding: 4,
                      color: '#7A8694',
                      fontFamily: 'inherit',
                    }}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {filtered.length === 0 ? (
              <div
                className="bg-surface border border-sand text-text-muted italic text-center"
                style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
              >
                {searchText
                  ? 'No events match your search.'
                  : 'No cancellation events in this window.'}
              </div>
            ) : (
              <>
                <LibTable
                  fixed
                  className="border border-sand"
                  style={{ borderRadius: 12, overflow: 'hidden' }}
                >
                  <colgroup>
                    <col />
                    <col style={{ width: '24%' }} />
                    <col style={{ width: 160 }} />
                  </colgroup>
                  <LibTable.Head>
                    <tr>
                      <LibTable.Th>Reason</LibTable.Th>
                      <LibTable.Th>User</LibTable.Th>
                      <LibTable.Th>Date &amp; Time</LibTable.Th>
                    </tr>
                  </LibTable.Head>
                  <tbody>
                    {filtered.map((r, idx) => {
                      const { date, time } = formatDateTime(r.timestamp || r.created_epoch);
                      return (
                        <LibTable.Row key={r.id || r.user_cancellation_id || idx}>
                          <LibTable.Td>
                            <div
                              className="text-navy"
                              style={{
                                fontSize: 13,
                                lineHeight: 1.4,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {r.reason || '—'}
                            </div>
                          </LibTable.Td>
                          <LibTable.Td>
                            <div className="text-navy" style={{ fontSize: 13, fontWeight: 600 }}>
                              {r.user_full_name || '—'}
                            </div>
                            <div className="font-mono text-text-muted truncate" style={{ fontSize: 11 }} title={r.user_email}>
                              {r.user_email}
                            </div>
                          </LibTable.Td>
                          <LibTable.Td>
                            <div className="text-navy" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                              {date}
                            </div>
                            <div className="font-mono text-text-muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                              {time}
                            </div>
                          </LibTable.Td>
                        </LibTable.Row>
                      );
                    })}
                  </tbody>
                </LibTable>
                <Pager
                  pagination={pagerPagination}
                  onPrev={handlePrev}
                  onNext={handleNext}
                  onPerPage={handlePerPage}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
